// Shared formatters for membership status / token-ledger rendering.
// Used by TopBar's user dropdown and the /billing page so the two surfaces
// stay in sync.

import type { Membership, SubscriptionPlan, SubscriptionStatus } from './types';

export interface LedgerRow {
  id: string;
  delta: number;
  source: LedgerSource;
  note: string | null;
  created_at: string;
}

export type LedgerSource =
  | 'trial_grant'
  | 'monthly_drip'
  | 'bulk_10'
  | 'bulk_100'
  | 'spend'
  | 'refund'
  | 'admin_adjustment';

export interface MembershipBadge {
  kind: 'trial' | 'active' | 'past-due' | 'canceled' | 'expired' | 'none';
  label: string;
}

const DAY_MS = 86_400_000;

export function membershipBadge(m: Membership | null): MembershipBadge {
  if (!m) return { kind: 'none', label: 'No membership' };
  switch (m.status) {
    case 'trial':     return { kind: 'trial',    label: 'Trial' };
    case 'active':    return { kind: 'active',   label: 'Active' };
    case 'past_due':  return { kind: 'past-due', label: 'Payment past due' };
    case 'canceled': {
      const ends = new Date(m.period_end).getTime();
      return ends > Date.now()
        ? { kind: 'canceled', label: 'Canceled — access ends soon' }
        : { kind: 'expired',  label: 'Expired' };
    }
    case 'expired':   return { kind: 'expired',  label: 'Expired' };
    default:          return { kind: 'none',     label: 'Inactive' };
  }
}

/** Short status-specific subtitle, e.g. "5 days left" or "Renews Jun 12, 2026". */
export function membershipDetail(m: Membership | null): string {
  if (!m) return '';
  const end = new Date(m.period_end);
  const fmt = end.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  const msLeft = end.getTime() - Date.now();
  switch (m.status) {
    case 'trial': {
      if (msLeft <= 0) return 'Trial expired';
      const days = Math.max(1, Math.ceil(msLeft / DAY_MS));
      return `${days} day${days === 1 ? '' : 's'} left`;
    }
    case 'active':   return `Renews ${fmt}`;
    case 'past_due': return `Last attempt failed — renewal due ${fmt}`;
    case 'canceled': return msLeft > 0 ? `Access ends ${fmt}` : `Ended ${fmt}`;
    case 'expired':  return `Expired ${fmt}`;
    default:         return '';
  }
}

export function formatPlan(plan: SubscriptionPlan | null): string {
  switch (plan) {
    case 'monthly_500':  return 'Monthly · $500/mo';
    case 'annual_4800':  return 'Annual · $4,800/yr';
    default:             return '';
  }
}

export function sourceLabel(source: LedgerSource): string {
  switch (source) {
    case 'trial_grant':      return 'Trial credits';
    case 'monthly_drip':     return 'Monthly credits';
    case 'bulk_10':          return 'Bundle of 10';
    case 'bulk_100':         return 'Bundle of 100';
    case 'spend':            return 'Panel print / export';
    case 'refund':           return 'Refund';
    case 'admin_adjustment': return 'Adjustment';
    default:                 return source;
  }
}

// Re-export for callers that only need the status enum.
export type { Membership, SubscriptionStatus };
